import React, { useState } from 'react';
import { User, Menu, LogOut, Settings, Shield, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotificationsDropdown } from './NotificationsDropdown';
import { GlobalSearch } from './GlobalSearch';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ChangePasswordDialog } from '@/components/settings/ChangePasswordDialog';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';

interface HeaderProps {
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  onMobileMenuToggle?: () => void;
}

const Header: React.FC<HeaderProps> = ({ title, subtitle, headerActions, onMobileMenuToggle }) => {
  const { user, role, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const navigateWithPreviewToken = (path: string) => navigate(buildPathWithPreviewToken(path));

  const handleLogout = async () => {
    await signOut();
    navigateWithPreviewToken('/auth');
  };

  const getRoleBadge = () => {
    switch (role) {
      case 'admin':
        return <Badge variant="destructive" className="text-xs">Admin</Badge>;
      case 'gestor':
        return <Badge variant="default" className="text-xs">Gestor</Badge>;
      case 'profissional':
        return <Badge variant="secondary" className="text-xs">Profissional</Badge>;
      default:
        return null;
    }
  };

  const getUserDisplayName = () => {
    if (!user) return 'Usuário';
    return user.user_metadata?.name || user.email?.split('@')[0] || 'Usuário';
  };

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border/60">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Left section */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          {/* Botão hamburger mobile - área de toque 44px */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden min-h-[44px] min-w-[44px] active:scale-95 transition-transform flex-shrink-0"
            onClick={onMobileMenuToggle}
            aria-label="Abrir menu"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-xl font-bold font-display text-foreground truncate">{title}</h1>
            {subtitle && (
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>
          {/* Header actions (e.g., export buttons) */}
          {headerActions && (
            <div className="hidden md:flex items-center ml-4 flex-shrink-0">
              {headerActions}
            </div>
          )}
        </div>

        {/* Center - Search (hidden on mobile) */}
        <div className="hidden lg:flex flex-1 max-w-md mx-8">
          <GlobalSearch />
        </div>

        {/* Right section */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <NotificationsDropdown />
          
          {user ? (
            <div className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-4 border-l border-border">
              {/* User info visible on larger screens */}
              <div className="hidden md:flex flex-col items-end">
                <p className="text-sm font-medium">{getUserDisplayName()}</p>
                <div className="flex items-center gap-1">
                  {getRoleBadge()}
                </div>
              </div>
              
              {/* User menu dropdown - área de toque 44px */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:ring-offset-2 min-h-[44px] min-w-[44px] active:scale-95 transition-transform"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover border border-border shadow-lg z-50">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{getUserDisplayName()}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                      <div className="pt-1">{getRoleBadge()}</div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  {isAdmin && (
                    <>
                      <DropdownMenuItem 
                        onClick={() => navigateWithPreviewToken('/settings')}
                        className="cursor-pointer min-h-[44px]"
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Configurações</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigateWithPreviewToken('/auditoria')}
                        className="cursor-pointer min-h-[44px]"
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Auditoria</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  
                  <DropdownMenuItem 
                    onClick={() => setIsChangePasswordOpen(true)}
                    className="cursor-pointer min-h-[44px]"
                  >
                    <Key className="mr-2 h-4 w-4" />
                    <span>Alterar Senha</span>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-destructive focus:text-destructive min-h-[44px]"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateWithPreviewToken('/auth')}
              className="min-h-[44px]"
            >
              Entrar
            </Button>
          )}
        </div>
      </div>
      
      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={isChangePasswordOpen} 
        onOpenChange={setIsChangePasswordOpen} 
      />
    </header>
  );
};

export default Header;
